import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { useRef, type ReactNode } from 'react';
import YaxleLogo from '../../assets/yaxleLogoHorizontalWhite.svg';
import AuthCarBackground from '../../assets/authCarBackground.webp';
import { BRAND_NAME } from '../../constants/brand';
import { useCarGlassRipple } from './useCarGlassRipple';

/**
 * Auth shell (ENG-2227, design "2a — split statement panel, single login
 * method"): an Apple Blue car artwork panel on the left with the brand
 * statement, and the page's form on the right. Pages (Login, Forgot Password,
 * Invite) render their forms as children; footer links (privacy / contact)
 * slot into the bottom of the right panel.
 */
interface AuthLayoutProps {
    children: ReactNode;
    footer?: ReactNode;
}

const StatementPanel = () => {
    const subtitleRef = useRef<HTMLParagraphElement>(null);
    const { canvasRef, containerRef, hoverTargetRef } = useCarGlassRipple({
        imageUrl: AuthCarBackground,
        enabled: true,
        anchorRef: subtitleRef,
    });

    return (
        <Flex
            ref={containerRef}
            display={{ base: 'none', lg: 'flex' }}
            flex={1}
            minW={0}
            m={2}
            borderRadius="3xl"
            direction="column"
            bg="brand.solid"
            backgroundImage={`url(${AuthCarBackground})`}
            backgroundBlendMode="multiply"
            backgroundSize="100% auto"
            backgroundPosition="center calc(100% + 12vw)"
            backgroundRepeat="no-repeat"
            color="fg.onBrand"
            pl={{ lg: 20, xl: 24 }}
            pr={{ lg: 14, xl: 16 }}
            py={{ lg: 12, xl: 14 }}
            position="relative"
            overflow="hidden"
        >
            <Box
                as="canvas"
                ref={canvasRef}
                position="absolute"
                top={0}
                left={0}
                w="100%"
                h="100%"
                pointerEvents="none"
                opacity={0}
                transition="opacity 0.3s ease"
                aria-hidden="true"
            />

            <Box
                ref={hoverTargetRef}
                position="absolute"
                left={0}
                right={0}
                bottom={0}
                pointerEvents="none"
                aria-hidden="true"
            />

            <Box position="relative">
                <Image src={YaxleLogo} alt={BRAND_NAME} w="160px" h="auto" />
            </Box>

            <Flex flex={1} align="center" position="relative">
                <Box maxW="560px">
                    <Text
                        as="h1"
                        m={0}
                        textStyle="h1"
                        color="fg.onBrand"
                        sx={{ textWrap: 'pretty' }}
                    >
                        Operations Intelligence for Auto Repairers.
                    </Text>
                    <Text
                        ref={subtitleRef}
                        mt={5}
                        textStyle="body"
                        color="fg.onBrand"
                        maxW="400px"
                    >
                        Centralize and automate parts procurement end-to-end with one platform.
                    </Text>
                </Box>
            </Flex>
        </Flex>
    );
};

const AuthLayout = ({ children, footer }: AuthLayoutProps) => (
    // A fixed height (not minH) so a tall form scrolls inside the right panel; html/body don't scroll.
    <Flex h="100dvh" w="100vw" bg="white" overflow="hidden">
        <StatementPanel />

        <Flex
            w={{ base: '100%', lg: '50vw' }}
            flexShrink={0}
            direction="column"
            bg="white"
            position="relative"
            overflowY="auto"
        >
            <Flex flex={1} align="center" justify="center" px={{ base: 6, md: 12 }} py={12}>
                <Box w="100%" maxW="28rem">
                    {children}
                </Box>
            </Flex>
            {footer && (
                <Flex justify="center" pb={6} px={6} flexShrink={0}>
                    {footer}
                </Flex>
            )}
        </Flex>
    </Flex>
);

export default AuthLayout;
